import clsx from 'clsx';
import React from 'react';
import PricingPlansCard from '../../components/PricingPlansCard';
import Slider from '../../components/Slider';
import styles from './pricing-plans.module.scss';
import {
  StripeProductDto,
  CreateCheckoutSessionDto,
  ResponseCreateCheckoutSession,
} from '@EHSNavigator/backend';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import Loader from '../../components/Loader';

interface PricingPlansProps {
  otherClasses?: string;
  plans: StripeProductDto[];
}

const PricingPlans: React.FC<PricingPlansProps> = ({ otherClasses, plans }) => {
  const location = useLocation();
  const { StripeApi } = useApiClients();
  const query = new URLSearchParams(location?.search);
  const email = decodeURIComponent(query.get('email') || '');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const settings = {
    infinite: true,
    speed: 1000,
    slidesToShow: 2,
    centerMode: true,
    slidesToScroll: 1,
    dots: false,
    centerPadding: '60px',
    arrows: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerPadding: '180px',
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          centerPadding: '80px',
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          centerPadding: '16px',
        },
      },
    ],
  };

  const mutation = useMutation<
    ResponseCreateCheckoutSession,
    Error,
    CreateCheckoutSessionDto
  >({
    mutationFn: async (checkoutSession: CreateCheckoutSessionDto) => {
      const response = email
        ? await StripeApi.stripeControllerCreateCheckoutSessionUsingEmail({
            createCheckoutSessionUsingEmailDto: {
              email,
              priceId: checkoutSession?.priceId,
              trial: checkoutSession?.trial,
            },
          })
        : await StripeApi.stripeControllerCreateCheckoutSession({
            createCheckoutSessionDto: {
              ...checkoutSession,
            },
          });
      return response.data;
    },
    onSuccess: (data) => {
      localStorage.setItem('user', '');
      window.location.href = data?.session_url;
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const pricingPlansClasses = clsx(
    otherClasses,
    'flex flex-1 flex-col items-center pt-12 md:pt-32 lg:pt-28 w-full',
    plans?.length > 1 ? 'justify-start' : 'justify-center',
  );

  return (
    <div className={pricingPlansClasses} data-testid="pricing-plans">
      {mutation?.isPending && (
        <div>
          <Loader
            variant="compact"
            otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px]  !fixed !top-0 left-0 h-dvh"
          />
        </div>
      )}
      <div className="mb-2 flex flex-col items-center gap-2.5 text-center">
        <img
          src="/assets/images/logo.png"
          alt="logo"
          className="h-85 w-69 md:h-100 md:w-20 lg:h-150 lg:w-121"
        />
        {plans?.length > 1 ? (
          <h2 className="text-brand-byzantine font-bold">Pricing Plans</h2>
        ) : (
          <h2 className="text-brand-byzantine font-bold">Pricing Plan</h2>
        )}
      </div>
      <p className="text-card-backgrounds/70 font-inter-variable -tracking-text-area mb-2 text-base leading-4 font-semibold md:mb-12 md:text-sm lg:mb-12 lg:text-2xl lg:leading-5">
        Proceed with your subscription
      </p>
      <div
        className={clsx(
          'mx-auto max-w-1512 gap-6 lg:grid lg:px-16 xl:gap-9 xl:px-174',

          plans?.length > 1 ? 'hidden lg:grid-cols-3' : 'lg:grid-cols-1',
        )}
      >
        {plans?.map((node: StripeProductDto, index) => (
          <PricingPlansCard
            key={index}
            data={node}
            handleClickFreeTrial={() => {
              mutation.mutate({
                organizationId: user?.orgId,
                priceId: String(node.default_price),
                userId: user?.id,
                trial: true,
              });
            }}
            mode={plans?.length > 1 ? 'light' : 'dark'}
            handleClickPayStripe={() => {
              mutation.mutate({
                organizationId: user?.orgId,
                priceId: String(node.default_price),
                userId: user?.id,
                trial: false,
              });
            }}
          />
        ))}
      </div>
      <div
        className={clsx(
          'w-full pl-9',
          plans?.length > 1 ? 'block lg:hidden' : 'hidden',
        )}
      >
        <Slider
          customSettings={settings}
          customClass={styles.pricing_plan_slider}
        >
          {plans?.map((node, index) => (
            <div className="pr-9">
              <PricingPlansCard
                key={index}
                data={node}
                handleClickFreeTrial={() => {
                  mutation.mutate({
                    organizationId: user?.orgId,
                    priceId: String(node.default_price),
                    userId: user?.id,
                    trial: true,
                  });
                }}
                handleClickPayStripe={() => {
                  mutation.mutate({
                    organizationId: user?.orgId,
                    priceId: String(node.default_price),
                    userId: user?.id,
                    trial: false,
                  });
                }}
              />
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};

export default PricingPlans;
